/**
 * Patient Portal Authentication Utilities
 * Handles patient-specific authentication checks
 */

import { apiUtils } from './apiUtils';

/**
 * Check if patient is authenticated
 * @returns {Promise<{authenticated: boolean, patient: object|null}>}
 */
export async function checkPatientAuth() {
  try {
    // Check for unified token first, then fall back to patientToken
    const token = localStorage.getItem('token') || localStorage.getItem('patientToken');
    
    if (!token) {
      return { authenticated: false, patient: null };
    }

    // Check if the user is a patient
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      if (userData.userType === 'patient' || userData.role === 'patient') {
        // User is authenticated as a patient via unified auth
        return {
          authenticated: true,
          patient: userData
        };
      }
    }

    // Fallback to patient-specific auth check
    const response = await apiUtils.get('/api/patient/auth/me');
    
    if (response.success) {
      return {
        authenticated: true,
        patient: response.patient
      };
    }
    
    // Token is invalid, clear it
    localStorage.removeItem('patientToken');
    localStorage.removeItem('patientData');
    
    return { authenticated: false, patient: null };
    
  } catch (error) {
    console.error('[PatientAuth] Error checking authentication:', error);
    
    // Clear invalid tokens on error
    if (error.response?.status === 401) {
      localStorage.removeItem('patientToken');
      localStorage.removeItem('patientData');
    }
    
    return { authenticated: false, patient: null };
  }
}

/**
 * Logout patient
 */
export async function logoutPatient() {
  try {
    await apiUtils.post('/api/patient/auth/logout');
  } catch (error) {
    console.error('[PatientAuth] Error during logout:', error);
  } finally {
    // Always clear local storage
    localStorage.removeItem('patientToken');
    localStorage.removeItem('patientData');
    window.location.href = '/login';
  }
}

/**
 * Get current patient data from localStorage
 * @returns {object|null}
 */
export function getCurrentPatient() {
  try {
    const patientData = localStorage.getItem('patientData');
    return patientData ? JSON.parse(patientData) : null;
  } catch (error) {
    console.error('[PatientAuth] Error parsing patient data:', error);
    return null;
  }
}

/**
 * Update patient data in localStorage
 * @param {object} patientData 
 */
export function updatePatientData(patientData) {
  try {
    localStorage.setItem('patientData', JSON.stringify(patientData));
  } catch (error) {
    console.error('[PatientAuth] Error updating patient data:', error);
  }
}
